/** What the portal's own backend is asked for: how to sign in, who you are, and a relay token. */

export type PortalConfig = { auth: "clerk"; clerkPublishableKey: string } | { auth: "dev" };

export type Me = { email: string; auth: string; robot: string; operator: string };

export type RelaySession = {
  token: string;
  org: string;
  orgId: string;
  expiresAt: number; // unix seconds
  wssUrl: string;
};

type TokenSource = () => Promise<string | null>;

// Where a signed-in page's session token comes from: Clerk's, once it is loaded. Unset in
// development, where the backend signs everyone in and there is nothing to send.
let sessionToken: TokenSource | null = null;

export function sendSessionTokenFrom(source: TokenSource | null): void {
  sessionToken = source;
}

async function get<T>(path: string): Promise<T> {
  const headers: Record<string, string> = {};
  // Clerk's tokens last about a minute and its script keeps one fresh, so asking every time
  // costs nothing and never sends a stale one.
  const token = sessionToken ? await sessionToken() : null;
  if (token) headers.Authorization = `Bearer ${token}`;
  const response = await fetch(path, { cache: "no-store", credentials: "same-origin", headers });
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    let detail = text;
    try {
      detail = (JSON.parse(text) as { detail?: string }).detail ?? text;
    } catch {
      // not JSON: a proxy's page, say. The text is still the best description there is.
    }
    throw new HttpError(response.status, detail || response.statusText);
  }
  return (await response.json()) as T;
}

export class HttpError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
  }
}

export const fetchConfig = () => get<PortalConfig>("/api/config");
export const fetchMe = () => get<Me>("/api/me");
export const fetchRelaySession = () => get<RelaySession>("/api/adamo/session");
